import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import SchemaMetadataCard from './SchemaMetadataCard';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';

/**
 * SchemaSelectionComponent - Reusable schema selector with metadata cards
 *
 * SCHEMA-FIRST ONLY: This component is designed for schema-first selection.
 * All tables in selected schemas are automatically included.
 *
 * Features:
 * - Schema list with rich metadata cards
 * - Search and filter functionality
 * - Multi-select support
 * - Visual feedback for selections
 * - Tier status indicators
 * - Automatic new table detection
 *
 * @param {Object} props
 * @param {string} props.connectionId - Database connection ID
 * @param {string[]} props.selectedSchemas - Array of selected schema names
 * @param {function} props.onSelectionChange - Callback when selection changes
 * @param {boolean} props.showMetadata - Whether to show detailed metadata
 * @param {number} props.maxSelections - Maximum number of schemas that can be selected
 * @param {boolean} props.multiSelect - Whether multiple selection is allowed (default: true)
 * @param {Object} props.schemas - Schema data (if pre-fetched)
 * @param {boolean} props.loading - Loading state (if controlled externally)
 */
const SchemaSelectionComponent = ({
  connectionId,
  selectedSchemas = [],
  onSelectionChange,
  showMetadata = true,
  maxSelections = null,
  multiSelect = true,
  schemas: externalSchemas = null,
  loading: externalLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name, table_count, size

  // Use external data if provided, otherwise would fetch via hook
  // For now, we'll use external data pattern to be flexible
  const schemas = externalSchemas || [];
  const loading = externalLoading;

  // Filter and sort schemas
  const filteredSchemas = useMemo(() => {
    let filtered = schemas.filter(schema => {
      const matchesSearch = !searchTerm ||
        schema.name?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    // Sort schemas
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'table_count':
          return (b.table_count || 0) - (a.table_count || 0);
        case 'size':
          return (b.size_bytes || 0) - (a.size_bytes || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [schemas, searchTerm, sortBy]);

  // Check if a schema is selected
  const isSelected = (schemaName) => {
    return selectedSchemas.includes(schemaName);
  };

  // Handle selection toggle
  const handleToggleSelection = (schemaName) => {
    if (!onSelectionChange) return;

    if (multiSelect) {
      if (isSelected(schemaName)) {
        // Deselect
        onSelectionChange(selectedSchemas.filter(s => s !== schemaName));
      } else {
        // Select (check max limit)
        if (maxSelections && selectedSchemas.length >= maxSelections) {
          // TODO: Show toast notification
          console.warn(`Maximum ${maxSelections} schemas can be selected`);
          return;
        }
        onSelectionChange([...selectedSchemas, schemaName]);
      }
    } else {
      // Single select
      onSelectionChange(isSelected(schemaName) ? [] : [schemaName]);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (!onSelectionChange) return;

    if (selectedSchemas.length === filteredSchemas.length) {
      // Deselect all
      onSelectionChange([]);
    } else {
      // Select all (respecting max limit)
      const schemasToSelect = filteredSchemas
        .slice(0, maxSelections || filteredSchemas.length)
        .map(s => s.name);
      onSelectionChange(schemasToSelect);
    }
  };

  // Calculate selection stats
  const selectionStats = useMemo(() => {
    const selectedSchemaObjs = schemas.filter(s => selectedSchemas.includes(s.name));
    const totalTables = selectedSchemaObjs.reduce((sum, s) => sum + (s.table_count || 0), 0);
    const totalSize = selectedSchemaObjs.reduce((sum, s) => sum + (s.size_bytes || 0), 0);

    return {
      schemaCount: selectedSchemas.length,
      tableCount: totalTables,
      sizeBytes: totalSize
    };
  }, [schemas, selectedSchemas]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" message="Loading schemas..." />
      </div>
    );
  }

  if (!schemas || schemas.length === 0) {
    return (
      <EmptyState
        title="No schemas found"
        description="No schemas are available for this connection. Try running metadata discovery first."
        icon={FunnelIcon}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter Controls */}
      <div className="flex items-center space-x-4">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Search schemas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Sort */}
        <div className="flex items-center space-x-2">
          <label htmlFor="sort" className="text-sm font-medium text-gray-700">
            Sort by:
          </label>
          <select
            id="sort"
            className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Name</option>
            <option value="table_count">Table Count</option>
            <option value="size">Size</option>
          </select>
        </div>

        {/* Select All (multi-select mode only) */}
        {multiSelect && (
          <button
            onClick={handleSelectAll}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {selectedSchemas.length === filteredSchemas.length ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {/* Selection Summary */}
      {selectedSchemas.length > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-md p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-primary-900">
              <span className="font-medium">
                {selectionStats.schemaCount} {selectionStats.schemaCount === 1 ? 'schema' : 'schemas'} selected
              </span>
              <span className="text-primary-700">
                {selectionStats.tableCount} {selectionStats.tableCount === 1 ? 'table' : 'tables'}
              </span>
              <span className="text-primary-700">
                {formatBytes(selectionStats.sizeBytes)}
              </span>
            </div>
            {maxSelections && (
              <span className="text-sm text-primary-700">
                {selectedSchemas.length} / {maxSelections} limit
              </span>
            )}
          </div>
        </div>
      )}

      {/* Schema Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSchemas.map((schema) => (
          <SchemaMetadataCard
            key={schema.name}
            schema={schema}
            isSelected={isSelected(schema.name)}
            onToggleSelection={() => handleToggleSelection(schema.name)}
            showMetadata={showMetadata}
            selectable={true}
          />
        ))}
      </div>

      {/* No Results */}
      {filteredSchemas.length === 0 && searchTerm && (
        <div className="text-center py-12">
          <p className="text-sm text-gray-500">
            No schemas match "{searchTerm}"
          </p>
          <button
            onClick={() => setSearchTerm('')}
            className="mt-2 text-sm text-primary-600 hover:text-primary-500"
          >
            Clear search
          </button>
        </div>
      )}
    </div>
  );
};

// Helper function to format bytes
function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

export default SchemaSelectionComponent;
